import { gql } from '@apollo/client';
import _ from 'lodash';
import React from 'react';
import type { SearchViewHeader_PlatformsFragment } from '../graphql/generated/graphqlTypes';
import dockyBlank from '../images/docky-blank.svg';
import SearchPlatformExternalPlatformTab from './SearchPlatformExternalPlatformTab';
import SearchPlatformTab from './SearchPlatformTab';
import SearchPlatformTabs from './SearchPlatformTabs';
import SearchResultTypeFilter from './SearchResultTypeFilter';
import styles from './SearchViewHeader.module.scss';

const PLATFORMS_FRAGMENT = gql`
  fragment SearchViewHeader_platforms on ExternalSearchPlatform {
    ...SearchPlatformTabs_platforms
  }
  ${SearchPlatformTabs.fragments.platforms}
`;

type CountsByType = React.ComponentProps<
  typeof SearchResultTypeFilter
>['countsByType'];

interface SearchViewHeaderProps {
  activePlatformKey?: string;
  encouragedPlatformSet: Set<string>;
  platforms: SearchViewHeader_PlatformsFragment[];
  searchQuery: string;
  type?: string;
  externalCountsByType: CountsByType;
  slabCountsByType: CountsByType;
  slabAllResultCount: string | null;
  vertical: boolean;
  verticalExtra?: React.ReactNode;
}

const SearchViewHeader = ({
  activePlatformKey,
  encouragedPlatformSet,
  platforms,
  searchQuery,
  type,
  vertical,
  externalCountsByType,
  slabCountsByType,
  slabAllResultCount,
  verticalExtra,
}: SearchViewHeaderProps) => {
  const enabledPlatforms =
    platforms?.filter(
      (platform) =>
        platform.personalAuthorized ||
        platform.teamAuthorized ||
        encouragedPlatformSet.has(platform.platform),
    ) ?? [];

  const hasExternalTypedResults = _.sum(_.values(externalCountsByType)) > 0;
  const hasSlabTypedResults = Object.keys(slabCountsByType).length > 1;

  if (vertical) {
    return (
      <div className={styles.verticalHeaderSection}>
        <SearchPlatformTab
          icon={<img src={dockyBlank} height="16" alt="Slab Logo" />}
          name="Slab"
          meta={slabAllResultCount}
          className={styles.tab}
          activeClassName={styles.activeTab}
          vertical
        />
        {!activePlatformKey && hasSlabTypedResults && (
          <SearchResultTypeFilter
            type={type}
            vertical
            countsByType={slabCountsByType}
          />
        )}
        {enabledPlatforms.map(({ platform }) => (
          <React.Fragment key={platform}>
            <SearchPlatformExternalPlatformTab
              active={activePlatformKey === platform}
              encourageInstallation={encouragedPlatformSet.has(platform)}
              key={platform}
              platformKey={platform}
              searchQuery={searchQuery}
              className={styles.tab}
              activeClassName={styles.activeTab}
              iconHeight={16}
              vertical
            />
            {activePlatformKey === platform && hasExternalTypedResults && (
              <SearchResultTypeFilter
                type={type}
                vertical
                countsByType={externalCountsByType}
              />
            )}
          </React.Fragment>
        ))}
        {verticalExtra}
      </div>
    );
  }
  return (
    <>
      <div className={styles.headerSection}>
        <SearchPlatformTabs
          activePlatform={activePlatformKey}
          encouragedPlatformSet={encouragedPlatformSet}
          enabledPlatforms={enabledPlatforms}
          searchQuery={searchQuery}
          slabResultCount={slabAllResultCount}
        />
      </div>
      {activePlatformKey
        ? hasExternalTypedResults && (
            <SearchResultTypeFilter
              type={type}
              countsByType={externalCountsByType}
            />
          )
        : hasSlabTypedResults && (
            <SearchResultTypeFilter
              type={type}
              countsByType={slabCountsByType}
            />
          )}
    </>
  );
};

SearchViewHeader.fragments = { platforms: PLATFORMS_FRAGMENT };

export default SearchViewHeader;
